'use client';

import { c as _c } from "react/compiler-runtime";
import { useAppForm } from '@repo/form';
import type { SavedViewSummary } from '@repo/schema/table-configuration';
import { useTableConfiguration } from '@repo/table/table-configuration';
import { Button, Label } from '@repo/ui/components';
import { isResponseError } from '@repo/upfetch';
import { useState } from 'react';
import { ActiveUserFetcher } from './active-user-fetcher.ts';
import { productError } from './product-error.ts';
type UserChoice = {
  id: number;
  last_name?: null | string;
  name: string;
};
type UserPick = {
  user: null | UserChoice;
};
const noUser: UserPick = {
  user: null
};
const fullName = (user: UserChoice) => `${user.name} ${user.last_name ?? ''}`.trim();

/** Owner hands the view to another active User; the numeric ID and everyone's pins stay. */
export const ViewTransfer = t0 => {
  const $ = _c(25);
  const {
    onCancel,
    onTransferred,
    view
  } = t0;
  const controller = useTableConfiguration();
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== controller || $[1] !== onTransferred || $[2] !== view) {
    t1 = {
      defaultValues: noUser,
      onSubmit: async t2 => {
        const {
          value
        } = t2;
        if (!value.user) {
          return setError("Choose the new owner.");
        }
        setError(null);
        ;
        try {
          onTransferred(await controller.transfer(view.id, value.user.id));
        } catch (t3) {
          const failure = t3;
          const ineligible = isResponseError(failure) && failure.status === 422;
          const message = productError(failure instanceof Error ? failure : new Error(String(failure)));
          setError(ineligible ? "That person can\u2019t own this view." : message);
        }
      }
    };
    $[0] = controller;
    $[1] = onTransferred;
    $[2] = view;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    AppField,
    AppForm,
    handleSubmit,
    Subscribe
  } = useAppForm(t1);
  let t2;
  if ($[4] !== handleSubmit) {
    t2 = event => {
      event.preventDefault();
      event.stopPropagation();
      handleSubmit();
    };
    $[4] = handleSubmit;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-xs text-muted-foreground">You keep access, but only the new owner can change or delete it. Pins stay as they are.</p>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Label data-sentry-element="Label" data-sentry-source-file="view-transfer.tsx">New owner</Label>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== AppField) {
    t5 = <div className="flex flex-col gap-1">{t4}<AppField name="user" data-sentry-element="AppField" data-sentry-source-file="view-transfer.tsx">{_temp}</AppField></div>;
    $[8] = AppField;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== error) {
    t6 = error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null;
    $[10] = error;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onCancel) {
    t7 = t8 => {
      const [user, isSubmitting] = t8;
      return <div className="flex justify-end gap-2"><Button disabled={isSubmitting} onClick={onCancel} type="button" variant="ghost">Cancel</Button><Button disabled={isSubmitting || !user} type="submit">{user ? `Transfer to ${fullName(user)}` : "Transfer"}</Button></div>;
    };
    $[12] = onCancel;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== Subscribe || $[15] !== t7) {
    t8 = <Subscribe selector={_temp2} data-sentry-element="Subscribe" data-sentry-source-file="view-transfer.tsx">{t7}</Subscribe>;
    $[14] = Subscribe;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t2 || $[18] !== t5 || $[19] !== t6 || $[20] !== t8) {
    t9 = <form className="flex flex-col gap-3" onSubmit={t2}>{t3}{t5}{t6}{t8}</form>;
    $[17] = t2;
    $[18] = t5;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== AppForm || $[23] !== t9) {
    t10 = <AppForm data-sentry-element="AppForm" data-sentry-component="ViewTransfer" data-sentry-source-file="view-transfer.tsx">{t9}</AppForm>;
    $[22] = AppForm;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
};
function _temp(field) {
  return <field.FormUserSelect label="Choose a person" UserFetcher={ActiveUserFetcher} />;
}
function _temp2(state) {
  return [state.values.user, state.isSubmitting] as const;
}